import useSWR from 'swr';
import { CleanBalance } from '../export-types/cleaned-types';
import { fetcher } from '../services/api';
import { formatDateValue } from '../utils/date-value';

export type SuppliersBalance = Array<{
  name: string;
  balanceDelivery: { cny: string; usd: string };
  balancePurchase: { cny: string; usd: string };
  deliveryCurrency: string;
  purchaseCurrency: string;
}>;

export const useBalance = () => {
  const { data, isLoading, mutate } = useSWR<CleanBalance>('/balance', fetcher);
  return { data, isLoading, mutate };
};

export const useSuppliersBalance = (
  onSuccess: (data: SuppliersBalance) => void,
  asOfDate?: Date | null,
) => {
  const { data, isLoading, mutate } = useSWR<SuppliersBalance>(
    {
      url: '/balance/suppliers',
      params: asOfDate
        ? {
            asOfDate: formatDateValue(asOfDate),
          }
        : undefined,
    },
    fetcher,
    {
      dedupingInterval: 6 * 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      onSuccess,
    },
  );
  return { data, isLoading, mutate };
};

type Bill = {
  cny: string;
  usd: string;
  bonus: string;
};

export type UsersBalance = {
  positive: Bill;
  negative: Bill;
  total: Bill;
  deliveryDebt: string;
};

export type PaidUnredeemedOrders = {
  ordersCount: number;
  totalPrice: string;
};

export const usePaidUnredeemedOrders = (
  onSuccess: (data: PaidUnredeemedOrders) => void,
  asOfDate?: Date | null,
) => {
  const { data, isLoading, mutate } = useSWR<PaidUnredeemedOrders>(
    {
      url: '/balance/paid-unredeemed-orders',
      params: asOfDate
        ? {
            asOfDate: formatDateValue(asOfDate),
          }
        : undefined,
    },
    fetcher,
    {
      dedupingInterval: 6 * 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      onSuccess,
    },
  );
  return { data, isLoading, mutate };
};

export const useUsersBalance = (
  onSuccess: (data: UsersBalance) => void,
  excludeIds?: Array<number>,
  asOfDate?: Date | null,
) => {
  const { data, isLoading, mutate } = useSWR<UsersBalance>(
    {
      url: '/balance/users',
      params: {
        excludeIds,
        ...(asOfDate && { asOfDate: formatDateValue(asOfDate) }),
      },
    },
    fetcher,
    {
      dedupingInterval: 6 * 60 * 60 * 1000,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      onSuccess,
    },
  );
  return { data, isLoading, mutate };
};
